import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import firebase from '../../../utilities/firebase';

// Compact replacement for the tall `NoteLine` component, used for the banks /
// stips / funding-stips checklists. The stored shape is identical — an array of
// { label, check } on the parent document — so it reads and writes the same data
// the old form did.
export default function NoteList({
  id,
  table = 'deals',
  property = 'items',
  entity = 'item',
  items = [],
  disabled,
}) {
  const [notes, setNotes] = React.useState(items);
  const [draft, setDraft] = React.useState('');

  React.useEffect(() => { setNotes(items); }, [items]);

  const persist = async (next) => {
    setNotes(next);
    if (id) await firebase.firestore().doc(`${table}/${id}`).set({ [property]: next }, { merge: true });
  };

  // Commas and newlines split into separate entries — pasting a list of banks in
  // one go is the common case.
  const add = async () => {
    const value = draft.trim();
    if (!value) return;
    const parts = (value.includes('\n') ? value.split('\n') : value.split(','))
      .map(x => x.trim())
      .filter(Boolean)
      .map(label => ({ label, check: false }));
    if (!parts.length) return;
    setDraft('');
    await persist([...notes, ...parts]);
  };

  const toggle = async (label) => {
    await persist(notes.map(n => n.label === label ? { ...n, check: !n.check } : n));
  };

  const remove = async (label) => {
    await persist(notes.filter(n => n.label !== label));
  };

  const done = notes.filter(n => n.check).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {notes.length === 0 ? 'None entered' : `${done} of ${notes.length} cleared`}
        </Typography>
      </Box>

      {notes.length > 0 && (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden', mb: 1 }}>
          {notes.map((note, i) => (
            <Box
              key={`${note.label}-${i}`}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                pl: 0.5, pr: 0.5, py: 0.25,
                borderBottom: i < notes.length - 1 ? 1 : 0, borderColor: 'divider',
                bgcolor: i % 2 === 1 ? 'grey.50' : 'transparent',
              }}
            >
              <Checkbox
                size="small"
                checked={!!note.check}
                onChange={() => toggle(note.label)}
                disabled={disabled}
              />
              <Typography
                variant="body2"
                sx={{
                  flex: 1, minWidth: 0,
                  textDecoration: note.check ? 'line-through' : 'none',
                  color: note.check ? 'text.disabled' : 'text.primary',
                }}
              >
                {note.label}
              </Typography>
              <IconButton size="small" color="error" onClick={() => remove(note.label)} disabled={disabled}>
                <DeleteIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 0.75 }}>
        <TextField
          size="small"
          fullWidth
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={`Add ${entity}`}
          disabled={disabled}
        />
        <IconButton size="small" onClick={add} disabled={disabled || !draft.trim()}>
          <AddIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
}
